import classnames from "classnames";
import { useUser } from "contexts/UserContext";
import FavouriteIcon from "images/bookmark-filled.svg?react";
import FavouriteOutlinedIcon from "images/bookmark.svg?react";
import LockIcon from "images/lock.svg?react";
import { routes } from "pages/routes";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { getTimeStringFromMinutes } from "utils/date";
import { courseTimeLeft } from "services/content/utils";
import CheckIcon from "../../../../../images/check_circle_grey.svg?react";
import LessonIcon from "../../../../../images/lesson.svg?react";
import TimeIcon from "@mui/icons-material/Schedule";
import { LibraryObject, LibraryObjectType } from "services/content/model";
import { useToggleFavourite } from "../../../../../services/favorites";
import { HStack } from "../../../../Stack";
import { LibraryObjectViewType } from "../../LibraryObjectViewType";
import bookImg from "./../../../../../images/book.svg";
import LevelIcon from "./../../../../../images/level.svg?react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

/**
 * What a card is given. Derived from the canonical model rather than restated, so the two
 * cannot drift; `Partial` because callers render previews of objects that do not exist yet.
 * `assignment` and `level` are not part of the library-object contract — the API never sends
 * them — and are supplied by callers that render one alongside the object.
 */
export type ObjectCardObject = Partial<LibraryObject> & {
  assignment?: { id?: string };
};

type ObjectCardInfoProps = {
  data: ObjectCardObject;
  isProgress?: boolean;
};

const ObjectCardInfo = (props: ObjectCardInfoProps) => {
  const { t } = useTranslation();
  const isUserNotStudent = !useUser().isStudent;

  const levelsTag = props.data.tags
    ?.filter(tag => tag.category === "level")
    .map(tag => tag.name);

  // Level shows the first of a list, with "Advanced" hybrids abbreviated
  // ("Advanced Beginner" -> "Adv Beginner").
  let lessonsInfo = "";
  if (isUserNotStudent) {
    lessonsInfo = t("Learn.objectCard.lessons", {
      count: props.data.children?.lessons || 0,
    });
  } else {
    const remaining = props.data.progress?.remaining.length || 0;
    const inProgress = Number(props.data.progress?.progress) > 0;
    lessonsInfo = t(
      inProgress ? "Learn.objectCard.lessonsLeft" : "Learn.objectCard.lessons",
      { count: remaining }
    );
  }

  const firstLevel = levelsTag?.[0];
  const levelLabel =
    firstLevel === undefined
      ? t("Learn.objectCard.noLevel")
      : firstLevel.replace(/^Advanced([\s-])/, "Adv$1");

  return (
    <div className="object-card-info">
      {!!Math.floor(Number(props.data.progress?.progress)) && (
        <div className="object-card-progress">
          <div className="progress-bar">
            <div
              className="progress-inner"
              style={{ width: `${props.data.progress?.progress || 0}%` }}
            />
          </div>
        </div>
      )}

      <div className="info-line">
        {Number(props.data.progress?.progress) === 100 ? (
          <div className="info-item">
            <CheckIcon />
            {t("Learn.objectCard.completedAll")}
          </div>
        ) : (
          <>
            <div className="info-item">
              <div className="info-line-icon">
                <TimeIcon />
              </div>
              <div>
                {!isUserNotStudent && props.data.type === LibraryObjectType.COURSE
                  ? courseTimeLeft(props.data.progress)
                  : getTimeStringFromMinutes(props.data.length)}
              </div>
            </div>
            {props.data.type === LibraryObjectType.COURSE && (
              <div className="info-item">
                <div className="info-line-icon">
                  <LessonIcon />
                </div>
                <div>{lessonsInfo}</div>
              </div>
            )}
            {Number(props.data.progress?.progress) > 0 && (
              <div className="info-item progress-info">
                {Math.floor(Number(props.data.progress?.progress))}%
              </div>
            )}
          </>
        )}
        {!props.isProgress && (
          <div className="info-item level">
            <div className="info-line-icon">
              <LevelIcon />
            </div>
            <div>{levelLabel}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export type ObjectCardProps = {
  object: ObjectCardObject;
  hasLesson?: boolean;
  ribbon?: string;
  type: LibraryObjectViewType;
  // Never rendered; the list still supplies it.
  isProgress?: boolean;
  isMyLearning?: boolean;
  isLocked?: boolean;
  onOpenPlan?: () => void;
};

const ObjectCard = (props: ObjectCardProps) => {
  const user = useUser();

  const { t } = useTranslation();
  const favourite = useToggleFavourite(props.object);

  const objectId = props.object.id;
  const canFavorite = !!objectId && user.isStudent;

  // The whole card is a link to the object, so the bookmark keeps the click.
  const handleFavorite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    favourite.toggle();
  };

  if (props.type === LibraryObjectViewType.HEADER && !props.object.description) {
    return null;
  }
  let image = null;

  if (props.hasLesson) {
    if (props.object.preview_url) {
      image = props.object.preview_url;
    } else {
      image = bookImg;
    }
  } else {
    if (props.type === LibraryObjectViewType.HEADER) {
      if (props.object.preview_url) {
        image = props.object.preview_url;
      }
    } else if (props.object.preview_vertical_url) {
      image = props.object.preview_vertical_url;
    } else {
      if (props.object.preview_url) {
        image = props.object.preview_url;
      } else {
        image = bookImg;
      }
    }
  }
  const previewImg = (
    <img
      src={image ?? undefined}
      alt="preview"
      className={props.hasLesson ? "img-preview" : "preview_horizontal_img"}
    />
  );

  return (
    <div className={"object-card-wrapper relative " + props.type}>
      <LinkToObject
        object={props.object}
        isMyLearning={props.isMyLearning}
        isLocked={props.isLocked}
        onOpenPlan={props.onOpenPlan}
      >
        {props.isLocked && (
          <>
            <div className={"locked-layer"}>
              <HStack justify={"center"} align={"center"} style={{ height: "100%" }}>
                <LockIcon />
              </HStack>
            </div>

            {
              <div className={"pro-course-badge"}>
                {t("Learn.courseLessons.proAccess")}
              </div>
            }

            <div className="object-card-type-locked">
              {t(
                props.object.type === LibraryObjectType.COURSE
                  ? "Common.labels.course"
                  : "Dashboard.objectCard.lesson"
              )}
            </div>
          </>
        )}

        {props.ribbon && (
          <div className="ribbon">
            <span>{props.ribbon}</span>
          </div>
        )}
        <div
          className={classnames("object-card", {
            "object-progress-card": props.isProgress,
          })}
        >
          <div className={`preview-container`}>{previewImg}</div>
          <div className="object-details">
            <div className="author-container">
              {!props.isLocked && (
                <div className="object-title horizontal">
                  <div className="object-card-type">
                    {t(
                      props.object.type === LibraryObjectType.COURSE
                        ? "Common.labels.course"
                        : "Dashboard.objectCard.lesson"
                    )}
                  </div>
                  {props.type !== LibraryObjectViewType.HEADER && canFavorite && (
                    <button
                      className={"bookmark-btn"}
                      onClick={handleFavorite}
                      style={{ flexShrink: 0 }}
                      aria-pressed={favourite.isFavourite}
                      aria-label={t(
                        props.object.type === LibraryObjectType.COURSE
                          ? "Learn.coursePage.bookmarkCourse"
                          : "Learn.subHeader.bookmarkLesson"
                      )}
                    >
                      {favourite.isFavourite ? (
                        <FavouriteIcon />
                      ) : (
                        <FavouriteOutlinedIcon />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="object-card-content">
              <div className="object-card-title">
                <span>{props.object.name}</span>
              </div>
              <ObjectCardInfo data={props.object} isProgress={props.isProgress} />
            </div>
          </div>
        </div>
        {props.type === LibraryObjectViewType.HEADER && (
          <ArrowDropDownIcon className={props.type} />
        )}
      </LinkToObject>
    </div>
  );
};

export default ObjectCard;

type LinkToObjectProps = {
  object: ObjectCardObject;
  isMyLearning?: boolean;
  isLocked?: boolean;
  onOpenPlan?: () => void;
  children: ReactNode;
};

const LinkToObject = (props: LinkToObjectProps) => {
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  if (props.isMyLearning) {
    const from = query.get("from");
    if (from !== "my_learning") {
      query.set("from", "my_learning");
    }
  }
  let path = "",
    search = "";

  const assignmentId = props.object.assignment?.id;
  const objectId = props.object.id;

  if (assignmentId) {
    path = routes.exerciseAssignmentDetails.path({ id: assignmentId });
  } else if (!objectId) {
    // No id yet e.g. the preview of a not-yet-created object in the create dialog.
    // Render the card without a link.
    return <span className="card-link">{props.children}</span>;
  } else if (props.object.type === LibraryObjectType.COURSE) {
    path = routes.courseObject.path({ id: objectId });
    search = query.toString() ? `?${query.toString()}` : "";
  } else if (props.object.type === LibraryObjectType.LESSON) {
    path = routes.lessonObject.path({ id: objectId });
    search = query.toString() ? `?${query.toString()}` : "";
  } else {
    path = routes.libraryObject.path({ id: objectId });
    search = query.toString() ? `?${query.toString()}` : "";
  }

  return (
    <Link className="card-link" to={{ pathname: path || "", search }}>
      {props.children}
    </Link>
  );
};
